import {
  Button,
  FlexGrid,
  getClassNameFactory,
  getRehydratableName,
  Responsive,
} from '@dcl/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { IVideoArticleMixedList } from './types';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';
import { KALTURA_VIDEO_INIT } from '../../api';
import { Article } from './Components/Article/Article';
import './ArticleVideoMixed.css';
import { Video } from './Components/Video/Video';
import { ButtonColor, ButtonKind } from '@dcl/ui/dist/elements/Button';
import fetchApi from '../../../../utils/fetchApi';
import { DESKTOP_WIDTH, MOBILE_WIDTH } from '../../mock';

export const ArticleVideoMixed: React.FC<IVideoArticleMixedList> = (props) => {
  const { partnerId, secretKey, privileges, articleVideoMixed } = props;
  const getClassName = getClassNameFactory(ArticleVideoMixed.displayName);
  const [userKeyKs, setUserKeyKs] = useState('');
  const [showAll, setShowAll] = useState(false);

  const handleShowMore = () => {
    setShowAll(!showAll);
  };

  const displayedArticles = showAll
    ? articleVideoMixed
    : articleVideoMixed.slice(0, 6);

  const ExtractKalturaScript = () => {
    const scriptSrc =
      'https://cdnapisec.kaltura.com/p/2563011/embedPlaykitJs/uiconf_id/49647463';
    const existingScript = document.querySelector(`script[src='${scriptSrc}']`);

    if (existingScript?.parentNode) {
      existingScript.parentNode.removeChild(existingScript);
    }

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.type = 'text/javascript';
    document.body.appendChild(script);
  };

  useEffect(() => {
    ExtractKalturaScript();
    initiateUserKeyApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initiateUserKeyApi = useCallback(async () => {
    const response = await fetchApi({
      baseHostUrl: KALTURA_VIDEO_INIT,
      options: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          format: 1,
          type: 2,
          partnerId: partnerId,
          secret: secretKey,
          privileges: privileges,
        }),
      },
    });

    const sessionKey = await response.text();
    setUserKeyKs(sessionKey.slice(1, -1));
  }, [partnerId, privileges, secretKey]);

  return (
    <div
      className={getClassName()}
      data-rehydratable={getRehydratableName(ArticleVideoMixed.displayName)}
      {...propsToDataAttributes(props)}
    >
      <FlexGrid
        className={getClassName({ descendantName: 'articleContainer' })}
      >
        <FlexGrid.Row
          className={getClassName({ descendantName: 'articleRows' })}
        >
          {displayedArticles.map((article, index) => {
            const keyIndex = index + 1;
            return article.format === 'Article' ||
              article.format.toLocaleUpperCase() === 'PDF' ||
              article.format.toLocaleUpperCase() === 'FILE' ||
              article.format === '' ? (
              <FlexGrid.Col
                key={keyIndex}
                md={4}
                lg={4}
                sm={12}
                className={getClassName({ descendantName: 'cardColumns' })}
              >
                <Article
                  articleHeading={article.articleHeading ?? ''}
                  articleSegmentationTitle={
                    article.articleSegmentationTitle ?? ''
                  }
                  articlesegmentationText={
                    article.articleSegmentationText ?? ''
                  }
                  articleFormat={article.format}
                  articleReadTime={article.articleReadTime ?? ''}
                  articleDescription={article.articleDescription ?? ''}
                  articleLinkHref={article.linkHref}
                  articleLinkText={article.linkText}
                  isNewTab={article.isNewTab ?? false}
                  badge={article.articleBadge ?? ''}
                />
              </FlexGrid.Col>
            ) : (
              <FlexGrid.Col
                key={keyIndex}
                md={4}
                lg={4}
                sm={12}
                className={getClassName({ descendantName: 'cardColumns' })}
              >
                {userKeyKs.length > 0 && (
                  <Video
                    linkHref={article.linkHref}
                    linkText={article.linkText}
                    format={article.format}
                    isNewTab={article.isNewTab}
                    entryId={article.entryId}
                    ks={userKeyKs}
                  />
                )}
              </FlexGrid.Col>
            );
          })}
        </FlexGrid.Row>
        {articleVideoMixed.length > 6 && (
          <div className={getClassName({ descendantName: 'buttonContainer' })}>
            <Responsive minWidth={DESKTOP_WIDTH}>
              <Button
                kind={ButtonKind.BUTTON}
                color={ButtonColor.SECONDARY}
                onClick={handleShowMore}
              >
                {showAll ? 'Show Less' : 'Show More'}
              </Button>
            </Responsive>
            <Responsive minWidth={0} maxWidth={MOBILE_WIDTH}>
              <Button
                kind={ButtonKind.BUTTON}
                color={ButtonColor.SECONDARY}
                onClick={handleShowMore}
                block={true}
              >
                {showAll ? 'Show Less' : 'Show More'}
              </Button>
            </Responsive>
          </div>
        )}
      </FlexGrid>
    </div>
  );
};

ArticleVideoMixed.displayName = 'ArticleVideoMixed';
